'use client'

import { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '@/providers/Theme'

type Anniversary60BlockProps = {
  blockType: 'anniversary60'
}

// Animation variants for staggered text animations
const createTextAnimation = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: { duration: 0.6, delay, ease: [0.4, 0, 0.2, 1] as const },
})

const createLuechingerAnimation = (delay: number) => ({
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.6, delay, ease: [0.4, 0, 0.2, 1] as const },
})

// SVG content for negative version (light mode)
const NegSVG = () => {
  // Animation variants
  const number60Anim = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] as const },
  }

  const textGroupAnim = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.6, delay: 0.2 },
  }

  const luechingerGroupAnim = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.6, delay: 0.4 },
  }

  // Create individual text animations with staggered delays
  const anim065 = createTextAnimation(0.65)
  const anim070 = createTextAnimation(0.7)
  const anim075 = createTextAnimation(0.75)
  const anim080 = createTextAnimation(0.8)
  const anim085 = createTextAnimation(0.85)
  const anim090 = createTextAnimation(0.9)
  const anim095 = createTextAnimation(0.95)
  const anim100 = createTextAnimation(1.0)
  const anim105 = createTextAnimation(1.05)
  const anim110 = createTextAnimation(1.1)
  const anim115 = createTextAnimation(1.15)
  const anim120 = createTextAnimation(1.2)
  const anim125 = createTextAnimation(1.25)
  const anim130 = createTextAnimation(1.3)
  const anim135 = createTextAnimation(1.35)
  const anim140 = createTextAnimation(1.4)
  const anim145 = createTextAnimation(1.45)
  const anim150 = createTextAnimation(1.5)
  const anim155 = createTextAnimation(1.55)

  // LÜCHINGER METALLBAU animations (x-axis)
  const luechinger080 = createLuechingerAnimation(0.8)
  const luechinger085 = createLuechingerAnimation(0.85)
  const luechinger090 = createLuechingerAnimation(0.9)
  const luechinger095 = createLuechingerAnimation(0.95)
  const luechinger100 = createLuechingerAnimation(1.0)
  const luechinger105 = createLuechingerAnimation(1.05)
  const luechinger110 = createLuechingerAnimation(1.1)
  const luechinger115 = createLuechingerAnimation(1.15)
  const luechinger120 = createLuechingerAnimation(1.2)
  const luechinger125 = createLuechingerAnimation(1.25)
  const luechinger130 = createLuechingerAnimation(1.3)
  const luechinger135 = createLuechingerAnimation(1.35)
  const luechinger140 = createLuechingerAnimation(1.4)

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1530.61 795.81"
      className="w-full h-full md:w-2/3 md:h-2/3"
    >
      <defs>
        <style>
          {`.cls-1 { fill: var(--brand); }
            .cls-2 { fill: var(--foreground); }`}
        </style>
      </defs>
      {/* Number "60" - animated */}
      <motion.g
        initial={number60Anim.initial}
        whileInView={number60Anim.animate}
        exit={number60Anim.exit}
        viewport={{ once: false }}
        transition={number60Anim.transition}
      >
        <path
          className="cls-1"
          d="M1131.37,418.38c6.25-14.07,15.19-26.95,26.53-37.97,22.83-22.18,52.95-34.4,84.8-34.4s61.96,12.21,84.8,34.4c11.35,11.02,20.29,23.9,26.53,37.97h152.28c-10.07-54.18-36.57-103.91-76.8-142.99-50.31-48.87-116.66-75.78-186.81-75.78s-136.5,26.91-186.81,75.78c-40.23,39.08-66.73,88.81-76.8,142.99h152.28Z"
        />
        <path
          className="cls-1"
          d="M505.74,477.45c.74-3.73,1.21-7.57,1.35-11.49,1.2-31.52,14.39-60.97,37.15-82.94,22.8-22.01,52.82-34.13,84.53-34.13s61.73,12.12,84.53,34.13c22.76,21.96,35.95,51.42,37.15,82.93.15,3.92.61,7.76,1.35,11.49h143.6c1.09-5.5,1.58-11.21,1.36-17.06-2.64-69.48-31.68-134.37-81.77-182.71-46.89-45.26-107.63-71.54-172.31-74.84L747.18,14.17l-166.66.31-173.02,304.6c-28.47,41.43-44.77,90.12-46.71,141.31-.22,5.85.27,11.56,1.36,17.06h143.6Z"
        />
      </motion.g>
      {/* "60 JAHRE" text - animated with stagger */}
      <motion.g
        initial={textGroupAnim.initial}
        whileInView={textGroupAnim.animate}
        exit={textGroupAnim.exit}
        viewport={{ once: false }}
        transition={textGroupAnim.transition}
      >
        <motion.g
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
        >
          <polygon
            className="cls-1"
            points="147.85 466.96 68.57 546.24 104.32 581.99 147.99 538.31 245.87 636.18 281.47 600.58 147.85 466.96"
          />
          <polygon
            className="cls-2"
            points="147.79 733.88 227.07 654.6 191.32 618.85 147.64 662.53 49.77 564.66 14.17 600.26 147.79 733.88"
          />
        </motion.g>
        <motion.g
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
        >
          {/* Rest of "60 JAHRE" paths - animated with fade-in from below */}
          <motion.path
            className="cls-2"
            d="m404.08 727.51-19.33 28.97-19.33-28.97h-6.07v53.07h5.84v-42.91l19.33 28.36h.3l19.33-28.43v42.98h5.99v-53.07z"
            initial={anim065.initial}
            whileInView={anim065.animate}
            exit={anim065.exit}
            viewport={{ once: false }}
            transition={anim065.transition}
          />
          <motion.path
            className="cls-2"
            d="m427.04 727.51v53.07h38.74v-5.46h-32.74v-18.57h28.96v-5.45h-28.96v-18.13h32.36v-5.46z"
            initial={anim070.initial}
            whileInView={anim070.animate}
            exit={anim070.exit}
            viewport={{ once: false }}
            transition={anim070.transition}
          />
          <motion.path
            className="cls-2"
            d="m475.94 727.51v5.55h17.81v47.52h6.07v-47.52h17.81v-5.55z"
            initial={anim075.initial}
            whileInView={anim075.animate}
            exit={anim075.exit}
            viewport={{ once: false }}
            transition={anim075.transition}
          />
          <motion.path
            className="cls-2"
            d="m546.29 734.11 12.04 27.06h-24.18zm-2.73-6.98-24.19 53.45h6.14l6.29-14.03h28.89l6.21 14.03h6.45l-24.18-53.45z"
            initial={anim080.initial}
            whileInView={anim080.animate}
            exit={anim080.exit}
            viewport={{ once: false }}
            transition={anim080.transition}
          />
          <motion.path
            className="cls-2"
            d="m585.7 727.51v53.07h35.86v-5.54h-29.87v-47.53z"
            initial={anim085.initial}
            whileInView={anim085.animate}
            exit={anim085.exit}
            viewport={{ once: false }}
            transition={anim085.transition}
          />
          <motion.path
            className="cls-2"
            d="m634.52 727.51v53.07h35.85v-5.54h-29.86v-47.53z"
            initial={anim090.initial}
            whileInView={anim090.animate}
            exit={anim090.exit}
            viewport={{ once: false }}
            transition={anim090.transition}
          />
          <motion.path
            className="cls-2"
            d="m708.65 753.97v-.15c0-5.46 4.62-9.85 9.93-9.85s9.93 4.4 9.93 9.85v.15c0 5.46-4.47 9.85-9.93 9.85s-9.93-4.39-9.93-9.85"
            initial={anim095.initial}
            whileInView={anim095.animate}
            exit={anim095.exit}
            viewport={{ once: false }}
            transition={anim095.transition}
          />
          <motion.path
            className="cls-2"
            d="m768.39 754.2v-.15c0-14.55 10.69-27.44 26.6-27.44 8.95 0 14.49 2.58 19.79 7.05l-3.86 4.55c-4.1-3.56-8.65-6.06-16.15-6.06-11.75 0-20.17 9.93-20.17 21.76v.15c0 12.66 8.11 22.06 21.01 22.06 6.06 0 11.67-2.35 15.32-5.31v-13.19h-16.15v-5.38h21.91v21.15c-4.93 4.4-12.36 8.11-21.31 8.11-16.68 0-26.98-12.13-26.98-27.29"
            initial={anim100.initial}
            whileInView={anim100.animate}
            exit={anim100.exit}
            viewport={{ once: false }}
            transition={anim100.transition}
          />
          <motion.path
            className="cls-2"
            d="m832.06 727.51v53.07h35.86v-5.54h-29.87v-47.53z"
            initial={anim105.initial}
            whileInView={anim105.animate}
            exit={anim105.exit}
            viewport={{ once: false }}
            transition={anim105.transition}
          />
          <motion.path
            className="cls-2"
            d="m903.24 734.11 12.06 27.06h-24.19zm-2.73-6.98-24.18 53.45h6.14l6.29-14.03h28.89l6.21 14.03h6.45l-24.19-53.45z"
            initial={anim110.initial}
            whileInView={anim110.animate}
            exit={anim110.exit}
            viewport={{ once: false }}
            transition={anim110.transition}
          />
          <motion.path
            className="cls-2"
            d="m938.11 772.85 3.71-4.4c5.53 5.01 10.84 7.51 18.19 7.51s11.83-3.79 11.83-9.02v-.15c0-4.93-2.66-7.73-13.8-10.09-12.2-2.65-17.81-6.59-17.81-15.31v-.15c0-8.34 7.35-14.47 17.43-14.47 7.73 0 13.27 2.19 18.65 6.51l-3.48 4.63c-4.93-4.02-9.86-5.76-15.32-5.76-6.89 0-11.29 3.79-11.29 8.56v.15c0 5 2.73 7.81 14.41 10.31 11.83 2.58 17.29 6.89 17.29 15.01v.15c0 9.1-7.58 15.01-18.12 15.01-8.42 0-15.32-2.8-21.68-8.49"
            initial={anim115.initial}
            whileInView={anim115.animate}
            exit={anim115.exit}
            viewport={{ once: false }}
            transition={anim115.transition}
          />
          <motion.path
            className="cls-2"
            d="m1017.32 753.97v-.15c0-5.46 4.62-9.85 9.93-9.85s9.93 4.4 9.93 9.85v.15c0 5.46-4.47 9.85-9.93 9.85s-9.93-4.39-9.93-9.85"
            initial={anim120.initial}
            whileInView={anim120.animate}
            exit={anim120.exit}
            viewport={{ once: false }}
            transition={anim120.transition}
          />
          <motion.path
            className="cls-2"
            d="m1076.29 727.51v5.55h17.82v47.52h6.06v-47.52h17.82v-5.55z"
            initial={anim125.initial}
            whileInView={anim125.animate}
            exit={anim125.exit}
            viewport={{ once: false }}
            transition={anim125.transition}
          />
          <motion.path
            className="cls-2"
            d="m1130.26 758.37v-30.86h5.99v30.48c0 11.45 6.06 17.89 16.22 17.89s16-5.91 16-17.51v-30.86h5.99v30.4c0 15.46-8.87 23.5-22.14 23.5s-22.06-8.04-22.06-23.04m11.53-42.15h7.05v6.82h-7.05zm14.18 0h7.05v6.82h-7.05z"
            initial={anim130.initial}
            whileInView={anim130.animate}
            exit={anim130.exit}
            viewport={{ once: false }}
            transition={anim130.transition}
          />
          <motion.path
            className="cls-2"
            d="m1190.52 727.51h22.83c6.51 0 11.74 1.97 15.08 5.31 2.58 2.58 4.09 6.3 4.09 10.46v.16c0 8.79-6.06 13.94-14.4 15.46l16.3 21.68h-7.35l-15.39-20.62h-15.17v20.62h-5.99zm22.3 27.07c7.96 0 13.65-4.1 13.65-10.92v-.15c0-6.51-5-10.45-13.57-10.45h-16.38v21.53h16.3z"
            initial={anim135.initial}
            whileInView={anim135.animate}
            exit={anim135.exit}
            viewport={{ once: false }}
            transition={anim135.transition}
          />
          <motion.path
            className="cls-2"
            d="m1304.39 773.08c-4.85 5.23-10.46 8.26-17.36 8.26-9.55 0-16.68-5.99-16.68-14.86v-.15c0-7.05 4.71-12.12 12.82-15.31-3.64-4.24-5.3-7.51-5.3-11.68v-.15c0-6.97 5.6-12.58 14.02-12.58 7.43 0 12.89 5.38 12.89 11.98v.15c0 6.75-4.78 11.07-12.51 13.79l12.13 12.36c2.35-3.41 4.47-7.43 6.37-11.83l4.93 2.28c-2.36 5.01-4.78 9.55-7.66 13.34l9.24 9.47-4.55 3.49zm-3.64-3.72-14.17-14.63c-7.05 2.58-10.16 6.9-10.16 11.37v.15c0 5.68 4.55 10 11.07 10 5 0 9.4-2.65 13.27-6.9m-1.6-30.32v-.15c0-4.24-3.18-7.35-7.51-7.35-4.78 0-8.04 3.33-8.04 7.65v.15c0 3.11 1.29 5.46 5.31 9.78 6.75-2.27 10.23-5.53 10.23-10.09"
            initial={anim140.initial}
            whileInView={anim140.animate}
            exit={anim140.exit}
            viewport={{ once: false }}
            transition={anim140.transition}
          />
          <motion.path
            className="cls-2"
            d="m1349.95 727.51v5.55h17.81v47.52h6.07v-47.52h17.81v-5.55z"
            initial={anim145.initial}
            whileInView={anim145.animate}
            exit={anim145.exit}
            viewport={{ once: false }}
            transition={anim145.transition}
          />
          <motion.path
            className="cls-2"
            d="m1398.77 754.2v-.15c0-14.63 10.99-27.44 27.14-27.44s26.98 12.66 26.98 27.3v.15c0 14.63-10.99 27.44-27.14 27.44s-26.98-12.66-26.98-27.29m47.91-.01v-.15c0-12.06-8.8-21.91-20.93-21.91s-20.77 9.7-20.77 21.76v.15c0 12.06 8.8 21.91 20.93 21.91s20.77-9.71 20.77-21.76"
            initial={anim150.initial}
            whileInView={anim150.animate}
            exit={anim150.exit}
            viewport={{ once: false }}
            transition={anim150.transition}
          />
          <motion.path
            className="cls-2"
            d="m1467.44 727.51h22.82c6.52 0 11.75 1.97 15.08 5.31 2.58 2.58 4.1 6.3 4.1 10.46v.16c0 8.79-6.07 13.94-14.41 15.46l16.3 21.68h-7.35l-15.39-20.62h-15.16v20.62h-5.99zm22.29 27.07c7.96 0 13.64-4.1 13.64-10.92v-.15c0-6.51-5-10.45-13.57-10.45h-16.38v21.53h16.3z"
            initial={anim155.initial}
            whileInView={anim155.animate}
            exit={anim155.exit}
            viewport={{ once: false }}
            transition={anim155.transition}
          />
          <path className="cls-2" d="m359.35 547.33v126.27h96.16v-30.66h-61.16v-95.61z" />
          <path
            className="cls-2"
            d="m681.3 644.2c-17.68 0-30.13-14.79-30.13-33.73v-.36c0-18.41 12.45-33.38 30.13-33.38 12.08 0 20.38 5.78 27.6 14.79l26.34-20.39c-11.19-15.51-27.79-26.34-53.58-26.34-38.24 0-66.56 28.86-66.56 65.67v.35c0 37.71 29.05 65.31 65.12 65.31 27.96 0 44.02-12.09 55.92-28.51l-26.34-18.76c-7.58 9.21-15.33 15.34-28.5 15.34"
          />
          <path
            className="cls-2"
            d="m834.91 594.6h-44.92v-47.27h-35v126.27h35v-47.98h44.92v47.98h35v-126.27h-35z"
          />
          <path className="cls-2" d="m903.32 547.32h35.18v126.28h-35.18z" />
          <path
            className="cls-2"
            d="m1056.47 614.07-51.95-66.74h-32.66v126.28h34.64v-69.28l53.94 69.28h30.67v-126.28h-34.64z"
          />
          <path
            className="cls-2"
            d="m1181.74 625.44h24.9v15.34c-5.23 3.42-11.72 5.23-20.2 5.23-20.03 0-34.28-14.62-34.28-35.01v-.35c0-19.3 14.07-34.63 32.29-34.63 12.45 0 21.47 4.33 30.48 11.89l20.57-24.7c-13.34-11.54-29.04-18.41-51.23-18.41-39.15 0-68.01 28.86-68.01 65.67v.36c0 38.25 29.41 65.3 68.74 65.3 22.9 0 41.49-8.48 54.84-19.48v-56.83h-58.09v25.62z"
          />
          <path
            className="cls-2"
            d="m1304.27 623.81h60.61v-27.6h-60.61v-19.1h66.93v-29.78h-101.56v126.28h102.46v-29.76h-67.83z"
          />
          <path
            className="cls-2"
            d="m1477.11 592.97c0 9.21-6.85 14.98-18.76 14.98h-23.09v-30.31h22.91c11.73 0 18.94 5.05 18.94 14.98zm9.2 36.62c15.7-6.67 25.98-19.48 25.98-38.78v-.37c0-12.44-3.79-22.18-11.19-29.39-8.47-8.67-21.82-13.72-41.13-13.72h-59.71v126.29h35v-38.25h15.33l25.43 38.25h40.41l-30.13-44.03z"
          />
          <path
            className="cls-2"
            d="m554.42 618.41c0 17.67-9.21 25.97-23.28 25.97s-23.27-8.65-23.27-26.87v-70.18h-35.53v70.9c0 39.5 22.54 57.73 58.44 57.73s59.18-17.86 59.18-58.64v-69.99h-35.54v71.07z"
          />
          <path
            className="cls-2"
            d="m487.15 514.43v-.26c0-9.87 8.22-17.71 17.84-17.71s17.97 7.84 17.97 17.71v.26c0 9.87-8.1 17.71-17.97 17.71s-17.84-7.84-17.84-17.71"
          />
          <path
            className="cls-2"
            d="m538.95 514.43v-.26c0-9.87 8.22-17.71 17.84-17.71s17.97 7.84 17.97 17.71v.26c0 9.87-8.1 17.71-17.97 17.71s-17.84-7.84-17.84-17.71"
          />
        </motion.g>
      </motion.g>
      {/* "LÜCHINGER METALLBAU" text - animated with stagger */}
      <motion.g
        initial={luechingerGroupAnim.initial}
        whileInView={luechingerGroupAnim.animate}
        exit={luechingerGroupAnim.exit}
        viewport={{ once: false }}
        transition={luechingerGroupAnim.transition}
        style={{ fill: 'var(--brand)' }}
      >
        <motion.path
          d="m974.77 473.57 3.27-2.81c2.47 3.73 5 5.74 9.19 5.74 4.54 0 7.87-3.27 7.87-9.65v-26.93h4.59v26.87c0 4.71-1.38 8.27-3.67 10.57-2.24 2.18-5.28 3.33-8.84 3.33-6.03 0-9.88-2.99-12.4-7.12z"
          initial={luechinger080.initial}
          whileInView={luechinger080.animate}
          exit={luechinger080.exit}
          viewport={{ once: false }}
          transition={luechinger080.transition}
        />
        <motion.path
          d="m1013.58 471.5v-.11c0-6.37 5.17-9.7 12.69-9.7 3.79 0 6.43.52 9.07 1.26v-.92c0-5.28-3.21-8.04-8.78-8.04-3.5 0-6.26.86-9.02 2.12l-1.32-3.62c3.27-1.49 6.43-2.53 10.79-2.53s7.46 1.15 9.59 3.27 3.1 4.94 3.1 8.73v18.14h-4.36v-4.42c-2.07 2.64-5.45 5.05-10.68 5.05-5.57 0-11.08-3.16-11.08-9.25zm21.81-2.29v-2.76c-2.18-.63-5.05-1.26-8.67-1.26-5.51 0-8.67 2.41-8.67 5.97v.11c0 3.73 3.44 5.91 7.46 5.91 5.46 0 9.88-3.27 9.88-7.98z"
          initial={luechinger085.initial}
          whileInView={luechinger085.animate}
          exit={luechinger085.exit}
          viewport={{ once: false }}
          transition={luechinger085.transition}
        />
        <motion.path
          d="m1055.19 438.2h4.42v17.4c1.95-3.22 5.05-5.8 10.16-5.8 7.18 0 11.37 4.82 11.37 11.89v18.43h-4.42v-17.34c0-5.51-2.99-8.96-8.21-8.96s-8.9 3.73-8.9 9.3v17h-4.42z"
          initial={luechinger090.initial}
          whileInView={luechinger090.animate}
          exit={luechinger090.exit}
          viewport={{ once: false }}
          transition={luechinger090.transition}
        />
        <motion.path
          d="m1096.69 450.43h4.42v7.75c2.18-4.94 6.49-8.5 12.12-8.27v4.77h-.34c-6.49 0-11.77 4.65-11.77 13.61v11.83h-4.42v-29.69z"
          initial={luechinger095.initial}
          whileInView={luechinger095.animate}
          exit={luechinger095.exit}
          viewport={{ once: false }}
          transition={luechinger095.transition}
        />
        <motion.path
          d="m1137.91 476.96c4.08 0 6.95-1.67 9.36-4.19l2.76 2.47c-2.99 3.33-6.6 5.57-12.23 5.57-8.15 0-14.81-6.26-14.81-15.5 0-8.61 6.03-15.5 14.24-15.5 8.79 0 13.84 7 13.84 15.73 0 .4 0 .86-.06 1.55h-23.54c.63 6.26 5.23 9.88 10.45 9.88zm8.67-13.44c-.52-5.4-3.62-9.99-9.47-9.99-5.11 0-9.01 4.25-9.65 9.99z"
          initial={luechinger100.initial}
          whileInView={luechinger100.animate}
          exit={luechinger100.exit}
          viewport={{ once: false }}
          transition={luechinger100.transition}
        />
        <motion.path
          d="m1211.05 473.57 3.27-2.81c2.47 3.73 5 5.74 9.19 5.74 4.54 0 7.87-3.27 7.87-9.65v-26.93h4.59v26.87c0 4.71-1.38 8.27-3.67 10.57-2.24 2.18-5.28 3.33-8.84 3.33-6.03 0-9.88-2.99-12.4-7.12z"
          initial={luechinger105.initial}
          whileInView={luechinger105.animate}
          exit={luechinger105.exit}
          viewport={{ once: false }}
          transition={luechinger105.transition}
        />
        <motion.path
          d="m1251.63 468.86v-18.43h4.42v17.34c0 5.51 2.99 8.96 8.21 8.96s8.9-3.73 8.9-9.3v-17h4.36v29.69h-4.36v-5.17c-2.01 3.22-5.05 5.8-10.16 5.8-7.18 0-11.37-4.82-11.37-11.89z"
          initial={luechinger110.initial}
          whileInView={luechinger110.animate}
          exit={luechinger110.exit}
          viewport={{ once: false }}
          transition={luechinger110.transition}
        />
        <motion.path
          d="m1298.01 474.43v5.68h-4.42v-41.92h4.42v18.2c2.41-3.56 5.91-6.6 11.37-6.6 7.12 0 14.18 5.63 14.18 15.39v.11c0 9.7-7 15.45-14.18 15.45-5.51 0-9.07-2.99-11.37-6.32zm21.02-9.07v-.11c0-7.01-4.82-11.48-10.45-11.48s-10.74 4.65-10.74 11.43v.11c0 6.89 5.22 11.48 10.74 11.48s10.45-4.25 10.45-11.43z"
          initial={luechinger115.initial}
          whileInView={luechinger115.animate}
          exit={luechinger115.exit}
          viewport={{ once: false }}
          transition={luechinger115.transition}
        />
        <motion.path
          d="m1337.96 439.06h5.05v4.88h-5.05zm.29 11.37h4.42v29.69h-4.42z"
          initial={luechinger120.initial}
          whileInView={luechinger120.animate}
          exit={luechinger120.exit}
          viewport={{ once: false }}
          transition={luechinger120.transition}
        />
        <motion.path
          d="m1359.48 438.2h4.42v41.92h-4.42z"
          initial={luechinger125.initial}
          whileInView={luechinger125.animate}
          exit={luechinger125.exit}
          viewport={{ once: false }}
          transition={luechinger125.transition}
        />
        <motion.path
          d="m1378.07 471.5v-.11c0-6.37 5.17-9.7 12.69-9.7 3.79 0 6.43.52 9.07 1.26v-.92c0-5.28-3.21-8.04-8.78-8.04-3.5 0-6.26.86-9.02 2.12l-1.32-3.62c3.27-1.49 6.43-2.53 10.79-2.53s7.46 1.15 9.59 3.27 3.1 4.94 3.1 8.73v18.14h-4.36v-4.42c-2.07 2.64-5.45 5.05-10.68 5.05-5.57 0-11.08-3.16-11.08-9.25zm21.82-2.29v-2.76c-2.18-.63-5.05-1.26-8.67-1.26-5.51 0-8.67 2.41-8.67 5.97v.11c0 3.73 3.44 5.91 7.46 5.91 5.46 0 9.88-3.27 9.88-7.98zm-15.5-28.71h5.11v5.46h-5.11zm10.05 0h5.11v5.46h-5.11z"
          initial={luechinger130.initial}
          whileInView={luechinger130.animate}
          exit={luechinger130.exit}
          viewport={{ once: false }}
          transition={luechinger130.transition}
        />
        <motion.path
          d="m1419.23 468.86v-18.43h4.42v17.34c0 5.51 2.99 8.96 8.21 8.96s8.9-3.73 8.9-9.3v-17h4.36v29.69h-4.36v-5.17c-2.01 3.22-5.05 5.8-10.16 5.8-7.18 0-11.37-4.82-11.37-11.89z"
          initial={luechinger135.initial}
          whileInView={luechinger135.animate}
          exit={luechinger135.exit}
          viewport={{ once: false }}
          transition={luechinger135.transition}
        />
        <motion.path
          d="m1461.19 450.43h4.42v5c1.95-2.93 4.59-5.63 9.59-5.63s7.92 2.58 9.53 5.91c2.12-3.27 5.28-5.91 10.45-5.91 6.83 0 11.02 4.59 11.02 11.94v18.37h-4.42v-17.34c0-5.74-2.87-8.96-7.69-8.96-4.48 0-8.21 3.33-8.21 9.19v17.11h-4.36v-17.46c0-5.57-2.93-8.84-7.64-8.84s-8.27 3.9-8.27 9.36v16.94h-4.42v-29.69z"
          initial={luechinger140.initial}
          whileInView={luechinger140.animate}
          exit={luechinger140.exit}
          viewport={{ once: false }}
          transition={luechinger140.transition}
        />
      </motion.g>
    </svg>
  )
}

// SVG content for positive version (dark mode) - simplified inline version
const PosSVG = () => {
  // Animation variants - same as NegSVG
  const number60Anim = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] as const },
  }

  const textGroupAnim = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.6, delay: 0.2 },
  }

  const luechingerGroupAnim = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.6, delay: 0.4 },
  }

  // Create individual text animations with staggered delays
  const anim065 = createTextAnimation(0.65)
  const anim070 = createTextAnimation(0.7)
  const anim075 = createTextAnimation(0.75)
  const anim080 = createTextAnimation(0.8)
  const anim085 = createTextAnimation(0.85)
  const anim090 = createTextAnimation(0.9)
  const anim095 = createTextAnimation(0.95)
  const anim100 = createTextAnimation(1.0)
  const anim105 = createTextAnimation(1.05)
  const anim110 = createTextAnimation(1.1)
  const anim115 = createTextAnimation(1.15)
  const anim120 = createTextAnimation(1.2)
  const anim125 = createTextAnimation(1.25)
  const anim130 = createTextAnimation(1.3)
  const anim135 = createTextAnimation(1.35)
  const anim140 = createTextAnimation(1.4)
  const anim145 = createTextAnimation(1.45)
  const anim150 = createTextAnimation(1.5)
  const anim155 = createTextAnimation(1.55)

  // LÜCHINGER METALLBAU animations (x-axis)
  const luechinger080 = createLuechingerAnimation(0.8)
  const luechinger085 = createLuechingerAnimation(0.85)
  const luechinger090 = createLuechingerAnimation(0.9)
  const luechinger095 = createLuechingerAnimation(0.95)
  const luechinger100 = createLuechingerAnimation(1.0)
  const luechinger105 = createLuechingerAnimation(1.05)
  const luechinger110 = createLuechingerAnimation(1.1)
  const luechinger115 = createLuechingerAnimation(1.15)
  const luechinger120 = createLuechingerAnimation(1.2)
  const luechinger125 = createLuechingerAnimation(1.25)
  const luechinger130 = createLuechingerAnimation(1.3)
  const luechinger135 = createLuechingerAnimation(1.35)
  const luechinger140 = createLuechingerAnimation(1.4)

  return (
    <svg
      viewBox="0 0 1530.61 795.81"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full md:w-2/3 md:h-2/3"
    >
      <defs>
        <style>
          {`.cls-1 { fill: var(--brand); }
            .cls-2 { fill: var(--foreground); }`}
        </style>
      </defs>
      {/* Number "60" - animated */}
      <motion.g
        initial={number60Anim.initial}
        whileInView={number60Anim.animate}
        exit={number60Anim.exit}
        viewport={{ once: false }}
        transition={number60Anim.transition}
      >
        <path
          className="cls-1"
          d="M1131.37,418.38c6.25-14.07,15.19-26.95,26.53-37.97,22.83-22.18,52.95-34.4,84.8-34.4s61.96,12.21,84.8,34.4c11.35,11.02,20.29,23.9,26.53,37.97h152.28c-10.07-54.18-36.57-103.91-76.8-142.99-50.31-48.87-116.66-75.78-186.81-75.78s-136.5,26.91-186.81,75.78c-40.23,39.08-66.73,88.81-76.8,142.99h152.28Z"
        />
        <path
          className="cls-1"
          d="M505.74,477.45c.74-3.73,1.21-7.57,1.35-11.49,1.2-31.52,14.39-60.97,37.15-82.94,22.8-22.01,52.82-34.13,84.53-34.13s61.73,12.12,84.53,34.13c22.76,21.96,35.95,51.42,37.15,82.93.15,3.92.61,7.76,1.35,11.49h143.6c1.09-5.5,1.58-11.21,1.36-17.06-2.64-69.48-31.68-134.37-81.77-182.71-46.89-45.26-107.63-71.54-172.31-74.84L747.18,14.17l-166.66.31-173.02,304.6c-28.47,41.43-44.77,90.12-46.71,141.31-.22,5.85.27,11.56,1.36,17.06h143.6Z"
        />
      </motion.g>
      {/* "60 JAHRE" text - animated with stagger */}
      <motion.g
        initial={textGroupAnim.initial}
        whileInView={textGroupAnim.animate}
        exit={textGroupAnim.exit}
        viewport={{ once: false }}
        transition={textGroupAnim.transition}
      >
        <motion.g
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.4, 0, 0.2, 1] as const }}
        >
          <polygon
            className="cls-1"
            points="147.85 466.96 68.57 546.24 104.32 581.99 147.99 538.31 245.87 636.18 281.47 600.58 147.85 466.96"
          />
          <polygon
            className="cls-2"
            points="147.79 733.88 227.07 654.6 191.32 618.85 147.64 662.53 49.77 564.66 14.17 600.26 147.79 733.88"
          />
          {/* Rest of "60 JAHRE" paths - using the same structure as neg version */}
          <motion.path
            className="cls-2"
            d="m404.08 727.51-19.33 28.97-19.33-28.97h-6.07v53.07h5.84v-42.91l19.33 28.36h.3l19.33-28.43v42.98h5.99v-53.07z"
            initial={anim065.initial}
            whileInView={anim065.animate}
            exit={anim065.exit}
            viewport={{ once: false }}
            transition={anim065.transition}
          />
          <motion.path
            className="cls-2"
            d="m427.04 727.51v53.07h38.74v-5.46h-32.74v-18.57h28.96v-5.45h-28.96v-18.13h32.36v-5.46z"
            initial={anim070.initial}
            whileInView={anim070.animate}
            exit={anim070.exit}
            viewport={{ once: false }}
            transition={anim070.transition}
          />
          <motion.path
            className="cls-2"
            d="m475.94 727.51v5.55h17.81v47.52h6.07v-47.52h17.81v-5.55z"
            initial={anim075.initial}
            whileInView={anim075.animate}
            exit={anim075.exit}
            viewport={{ once: false }}
            transition={anim075.transition}
          />
          <motion.path
            className="cls-2"
            d="m546.29 734.11 12.04 27.06h-24.18zm-2.73-6.98-24.19 53.45h6.14l6.29-14.03h28.89l6.21 14.03h6.45l-24.18-53.45z"
            initial={anim080.initial}
            whileInView={anim080.animate}
            exit={anim080.exit}
            viewport={{ once: false }}
            transition={anim080.transition}
          />
          <motion.path
            className="cls-2"
            d="m585.7 727.51v53.07h35.86v-5.54h-29.87v-47.53z"
            initial={anim085.initial}
            whileInView={anim085.animate}
            exit={anim085.exit}
            viewport={{ once: false }}
            transition={anim085.transition}
          />
          <motion.path
            className="cls-2"
            d="m634.52 727.51v53.07h35.85v-5.54h-29.86v-47.53z"
            initial={anim090.initial}
            whileInView={anim090.animate}
            exit={anim090.exit}
            viewport={{ once: false }}
            transition={anim090.transition}
          />
          <motion.path
            className="cls-2"
            d="m708.65 753.97v-.15c0-5.46 4.62-9.85 9.93-9.85s9.93 4.4 9.93 9.85v.15c0 5.46-4.47 9.85-9.93 9.85s-9.93-4.39-9.93-9.85"
            initial={anim095.initial}
            whileInView={anim095.animate}
            exit={anim095.exit}
            viewport={{ once: false }}
            transition={anim095.transition}
          />
          <motion.path
            className="cls-2"
            d="m768.39 754.2v-.15c0-14.55 10.69-27.44 26.6-27.44 8.95 0 14.49 2.58 19.79 7.05l-3.86 4.55c-4.1-3.56-8.65-6.06-16.15-6.06-11.75 0-20.17 9.93-20.17 21.76v.15c0 12.66 8.11 22.06 21.01 22.06 6.06 0 11.67-2.35 15.32-5.31v-13.19h-16.15v-5.38h21.91v21.15c-4.93 4.4-12.36 8.11-21.31 8.11-16.68 0-26.98-12.13-26.98-27.29"
            initial={anim100.initial}
            whileInView={anim100.animate}
            exit={anim100.exit}
            viewport={{ once: false }}
            transition={anim100.transition}
          />
          <motion.path
            className="cls-2"
            d="m832.06 727.51v53.07h35.86v-5.54h-29.87v-47.53z"
            initial={anim105.initial}
            whileInView={anim105.animate}
            exit={anim105.exit}
            viewport={{ once: false }}
            transition={anim105.transition}
          />
          <motion.path
            className="cls-2"
            d="m903.24 734.11 12.06 27.06h-24.19zm-2.73-6.98-24.18 53.45h6.14l6.29-14.03h28.89l6.21 14.03h6.45l-24.19-53.45z"
            initial={anim110.initial}
            whileInView={anim110.animate}
            exit={anim110.exit}
            viewport={{ once: false }}
            transition={anim110.transition}
          />
          <motion.path
            className="cls-2"
            d="m938.11 772.85 3.71-4.4c5.53 5.01 10.84 7.51 18.19 7.51s11.83-3.79 11.83-9.02v-.15c0-4.93-2.66-7.73-13.8-10.09-12.2-2.65-17.81-6.59-17.81-15.31v-.15c0-8.34 7.35-14.47 17.43-14.47 7.73 0 13.27 2.19 18.65 6.51l-3.48 4.63c-4.93-4.02-9.86-5.76-15.32-5.76-6.89 0-11.29 3.79-11.29 8.56v.15c0 5 2.73 7.81 14.41 10.31 11.83 2.58 17.29 6.89 17.29 15.01v.15c0 9.1-7.58 15.01-18.12 15.01-8.42 0-15.32-2.8-21.68-8.49"
            initial={anim115.initial}
            whileInView={anim115.animate}
            exit={anim115.exit}
            viewport={{ once: false }}
            transition={anim115.transition}
          />
          <motion.path
            className="cls-2"
            d="m1017.32 753.97v-.15c0-5.46 4.62-9.85 9.93-9.85s9.93 4.4 9.93 9.85v.15c0 5.46-4.47 9.85-9.93 9.85s-9.93-4.39-9.93-9.85"
            initial={anim120.initial}
            whileInView={anim120.animate}
            exit={anim120.exit}
            viewport={{ once: false }}
            transition={anim120.transition}
          />
          <motion.path
            className="cls-2"
            d="m1076.29 727.51v5.55h17.82v47.52h6.06v-47.52h17.82v-5.55z"
            initial={anim125.initial}
            whileInView={anim125.animate}
            exit={anim125.exit}
            viewport={{ once: false }}
            transition={anim125.transition}
          />
          <motion.path
            className="cls-2"
            d="m1130.26 758.37v-30.86h5.99v30.48c0 11.45 6.06 17.89 16.22 17.89s16-5.91 16-17.51v-30.86h5.99v30.4c0 15.46-8.87 23.5-22.14 23.5s-22.06-8.04-22.06-23.04m11.53-42.15h7.05v6.82h-7.05zm14.18 0h7.05v6.82h-7.05z"
            initial={anim130.initial}
            whileInView={anim130.animate}
            exit={anim130.exit}
            viewport={{ once: false }}
            transition={anim130.transition}
          />
          <motion.path
            className="cls-2"
            d="m1190.52 727.51h22.83c6.51 0 11.74 1.97 15.08 5.31 2.58 2.58 4.09 6.3 4.09 10.46v.16c0 8.79-6.06 13.94-14.4 15.46l16.3 21.68h-7.35l-15.39-20.62h-15.17v20.62h-5.99zm22.3 27.07c7.96 0 13.65-4.1 13.65-10.92v-.15c0-6.51-5-10.45-13.57-10.45h-16.38v21.53h16.3z"
            initial={anim135.initial}
            whileInView={anim135.animate}
            exit={anim135.exit}
            viewport={{ once: false }}
            transition={anim135.transition}
          />
          <motion.path
            className="cls-2"
            d="m1304.39 773.08c-4.85 5.23-10.46 8.26-17.36 8.26-9.55 0-16.68-5.99-16.68-14.86v-.15c0-7.05 4.71-12.12 12.82-15.31-3.64-4.24-5.3-7.51-5.3-11.68v-.15c0-6.97 5.6-12.58 14.02-12.58 7.43 0 12.89 5.38 12.89 11.98v.15c0 6.75-4.78 11.07-12.51 13.79l12.13 12.36c2.35-3.41 4.47-7.43 6.37-11.83l4.93 2.28c-2.36 5.01-4.78 9.55-7.66 13.34l9.24 9.47-4.55 3.49zm-3.64-3.72-14.17-14.63c-7.05 2.58-10.16 6.9-10.16 11.37v.15c0 5.68 4.55 10 11.07 10 5 0 9.4-2.65 13.27-6.9m-1.6-30.32v-.15c0-4.24-3.18-7.35-7.51-7.35-4.78 0-8.04 3.33-8.04 7.65v.15c0 3.11 1.29 5.46 5.31 9.78 6.75-2.27 10.23-5.53 10.23-10.09"
            initial={anim140.initial}
            whileInView={anim140.animate}
            exit={anim140.exit}
            viewport={{ once: false }}
            transition={anim140.transition}
          />
          <motion.path
            className="cls-2"
            d="m1349.95 727.51v5.55h17.81v47.52h6.07v-47.52h17.81v-5.55z"
            initial={anim145.initial}
            whileInView={anim145.animate}
            exit={anim145.exit}
            viewport={{ once: false }}
            transition={anim145.transition}
          />
          <motion.path
            className="cls-2"
            d="m1398.77 754.2v-.15c0-14.63 10.99-27.44 27.14-27.44s26.98 12.66 26.98 27.3v.15c0 14.63-10.99 27.44-27.14 27.44s-26.98-12.66-26.98-27.29m47.91-.01v-.15c0-12.06-8.8-21.91-20.93-21.91s-20.77 9.7-20.77 21.76v.15c0 12.06 8.8 21.91 20.93 21.91s20.77-9.71 20.77-21.76"
            initial={anim150.initial}
            whileInView={anim150.animate}
            exit={anim150.exit}
            viewport={{ once: false }}
            transition={anim150.transition}
          />
          <motion.path
            className="cls-2"
            d="m1467.44 727.51h22.82c6.52 0 11.75 1.97 15.08 5.31 2.58 2.58 4.1 6.3 4.1 10.46v.16c0 8.79-6.07 13.94-14.41 15.46l16.3 21.68h-7.35l-15.39-20.62h-15.16v20.62h-5.99zm22.29 27.07c7.96 0 13.64-4.1 13.64-10.92v-.15c0-6.51-5-10.45-13.57-10.45h-16.38v21.53h16.3z"
            initial={anim155.initial}
            whileInView={anim155.animate}
            exit={anim155.exit}
            viewport={{ once: false }}
            transition={anim155.transition}
          />
          <path className="cls-2" d="m359.35 547.33v126.27h96.16v-30.66h-61.16v-95.61z" />
          <path
            className="cls-2"
            d="m681.3 644.2c-17.68 0-30.13-14.79-30.13-33.73v-.36c0-18.41 12.45-33.38 30.13-33.38 12.08 0 20.38 5.78 27.6 14.79l26.34-20.39c-11.19-15.51-27.79-26.34-53.58-26.34-38.24 0-66.56 28.86-66.56 65.67v.35c0 37.71 29.05 65.31 65.12 65.31 27.96 0 44.02-12.09 55.92-28.51l-26.34-18.76c-7.58 9.21-15.33 15.34-28.5 15.34"
          />
          <path
            className="cls-2"
            d="m834.91 594.6h-44.92v-47.27h-35v126.27h35v-47.98h44.92v47.98h35v-126.27h-35z"
          />
          <path className="cls-2" d="m903.32 547.32h35.18v126.28h-35.18z" />
          <path
            className="cls-2"
            d="m1056.47 614.07-51.95-66.74h-32.66v126.28h34.64v-69.28l53.94 69.28h30.67v-126.28h-34.64z"
          />
          <path
            className="cls-2"
            d="m1181.74 625.44h24.9v15.34c-5.23 3.42-11.72 5.23-20.2 5.23-20.03 0-34.28-14.62-34.28-35.01v-.35c0-19.3 14.07-34.63 32.29-34.63 12.45 0 21.47 4.33 30.48 11.89l20.57-24.7c-13.34-11.54-29.04-18.41-51.23-18.41-39.15 0-68.01 28.86-68.01 65.67v.36c0 38.25 29.41 65.3 68.74 65.3 22.9 0 41.49-8.48 54.84-19.48v-56.83h-58.09v25.62z"
          />
          <path
            className="cls-2"
            d="m1304.27 623.81h60.61v-27.6h-60.61v-19.1h66.93v-29.78h-101.56v126.28h102.46v-29.76h-67.83z"
          />
          <path
            className="cls-2"
            d="m1477.11 592.97c0 9.21-6.85 14.98-18.76 14.98h-23.09v-30.31h22.91c11.73 0 18.94 5.05 18.94 14.98zm9.2 36.62c15.7-6.67 25.98-19.48 25.98-38.78v-.37c0-12.44-3.79-22.18-11.19-29.39-8.47-8.67-21.82-13.72-41.13-13.72h-59.71v126.29h35v-38.25h15.33l25.43 38.25h40.41l-30.13-44.03z"
          />
          <path
            className="cls-2"
            d="m554.42 618.41c0 17.67-9.21 25.97-23.28 25.97s-23.27-8.65-23.27-26.87v-70.18h-35.53v70.9c0 39.5 22.54 57.73 58.44 57.73s59.18-17.86 59.18-58.64v-69.99h-35.54v71.07z"
          />
          <path
            className="cls-2"
            d="m487.15 514.43v-.26c0-9.87 8.22-17.71 17.84-17.71s17.97 7.84 17.97 17.71v.26c0 9.87-8.1 17.71-17.97 17.71s-17.84-7.84-17.84-17.71"
          />
          <path
            className="cls-2"
            d="m538.95 514.43v-.26c0-9.87 8.22-17.71 17.84-17.71s17.97 7.84 17.97 17.71v.26c0 9.87-8.1 17.71-17.97 17.71s-17.84-7.84-17.84-17.71"
          />
        </motion.g>
      </motion.g>
      {/* "LÜCHINGER METALLBAU" text - animated with stagger */}
      <motion.g
        initial={luechingerGroupAnim.initial}
        whileInView={luechingerGroupAnim.animate}
        exit={luechingerGroupAnim.exit}
        viewport={{ once: false }}
        transition={luechingerGroupAnim.transition}
        style={{ fill: 'var(--brand)' }}
      >
        <motion.path
          d="m974.77 473.57 3.27-2.81c2.47 3.73 5 5.74 9.19 5.74 4.54 0 7.87-3.27 7.87-9.65v-26.93h4.59v26.87c0 4.71-1.38 8.27-3.67 10.57-2.24 2.18-5.28 3.33-8.84 3.33-6.03 0-9.88-2.99-12.4-7.12z"
          initial={luechinger080.initial}
          whileInView={luechinger080.animate}
          exit={luechinger080.exit}
          viewport={{ once: false }}
          transition={luechinger080.transition}
        />
        <motion.path
          d="m1013.58 471.5v-.11c0-6.37 5.17-9.7 12.69-9.7 3.79 0 6.43.52 9.07 1.26v-.92c0-5.28-3.21-8.04-8.78-8.04-3.5 0-6.26.86-9.02 2.12l-1.32-3.62c3.27-1.49 6.43-2.53 10.79-2.53s7.46 1.15 9.59 3.27 3.1 4.94 3.1 8.73v18.14h-4.36v-4.42c-2.07 2.64-5.45 5.05-10.68 5.05-5.57 0-11.08-3.16-11.08-9.25zm21.81-2.29v-2.76c-2.18-.63-5.05-1.26-8.67-1.26-5.51 0-8.67 2.41-8.67 5.97v.11c0 3.73 3.44 5.91 7.46 5.91 5.46 0 9.88-3.27 9.88-7.98z"
          initial={luechinger085.initial}
          whileInView={luechinger085.animate}
          exit={luechinger085.exit}
          viewport={{ once: false }}
          transition={luechinger085.transition}
        />
        <motion.path
          d="m1055.19 438.2h4.42v17.4c1.95-3.22 5.05-5.8 10.16-5.8 7.18 0 11.37 4.82 11.37 11.89v18.43h-4.42v-17.34c0-5.51-2.99-8.96-8.21-8.96s-8.9 3.73-8.9 9.3v17h-4.42z"
          initial={luechinger090.initial}
          whileInView={luechinger090.animate}
          exit={luechinger090.exit}
          viewport={{ once: false }}
          transition={luechinger090.transition}
        />
        <motion.path
          d="m1096.69 450.43h4.42v7.75c2.18-4.94 6.49-8.5 12.12-8.27v4.77h-.34c-6.49 0-11.77 4.65-11.77 13.61v11.83h-4.42v-29.69z"
          initial={luechinger095.initial}
          whileInView={luechinger095.animate}
          exit={luechinger095.exit}
          viewport={{ once: false }}
          transition={luechinger095.transition}
        />
        <motion.path
          d="m1137.91 476.96c4.08 0 6.95-1.67 9.36-4.19l2.76 2.47c-2.99 3.33-6.6 5.57-12.23 5.57-8.15 0-14.81-6.26-14.81-15.5 0-8.61 6.03-15.5 14.24-15.5 8.79 0 13.84 7 13.84 15.73 0 .4 0 .86-.06 1.55h-23.54c.63 6.26 5.23 9.88 10.45 9.88zm8.67-13.44c-.52-5.4-3.62-9.99-9.47-9.99-5.11 0-9.01 4.25-9.65 9.99z"
          initial={luechinger100.initial}
          whileInView={luechinger100.animate}
          exit={luechinger100.exit}
          viewport={{ once: false }}
          transition={luechinger100.transition}
        />
        <motion.path
          d="m1211.05 473.57 3.27-2.81c2.47 3.73 5 5.74 9.19 5.74 4.54 0 7.87-3.27 7.87-9.65v-26.93h4.59v26.87c0 4.71-1.38 8.27-3.67 10.57-2.24 2.18-5.28 3.33-8.84 3.33-6.03 0-9.88-2.99-12.4-7.12z"
          initial={luechinger105.initial}
          whileInView={luechinger105.animate}
          exit={luechinger105.exit}
          viewport={{ once: false }}
          transition={luechinger105.transition}
        />
        <motion.path
          d="m1251.63 468.86v-18.43h4.42v17.34c0 5.51 2.99 8.96 8.21 8.96s8.9-3.73 8.9-9.3v-17h4.36v29.69h-4.36v-5.17c-2.01 3.22-5.05 5.8-10.16 5.8-7.18 0-11.37-4.82-11.37-11.89z"
          initial={luechinger110.initial}
          whileInView={luechinger110.animate}
          exit={luechinger110.exit}
          viewport={{ once: false }}
          transition={luechinger110.transition}
        />
        <motion.path
          d="m1298.01 474.43v5.68h-4.42v-41.92h4.42v18.2c2.41-3.56 5.91-6.6 11.37-6.6 7.12 0 14.18 5.63 14.18 15.39v.11c0 9.7-7 15.45-14.18 15.45-5.51 0-9.07-2.99-11.37-6.32zm21.02-9.07v-.11c0-7.01-4.82-11.48-10.45-11.48s-10.74 4.65-10.74 11.43v.11c0 6.89 5.22 11.48 10.74 11.48s10.45-4.25 10.45-11.43z"
          initial={luechinger115.initial}
          whileInView={luechinger115.animate}
          exit={luechinger115.exit}
          viewport={{ once: false }}
          transition={luechinger115.transition}
        />
        <motion.path
          d="m1337.96 439.06h5.05v4.88h-5.05zm.29 11.37h4.42v29.69h-4.42z"
          initial={luechinger120.initial}
          whileInView={luechinger120.animate}
          exit={luechinger120.exit}
          viewport={{ once: false }}
          transition={luechinger120.transition}
        />
        <motion.path
          d="m1359.48 438.2h4.42v41.92h-4.42z"
          initial={luechinger125.initial}
          whileInView={luechinger125.animate}
          exit={luechinger125.exit}
          viewport={{ once: false }}
          transition={luechinger125.transition}
        />
        <motion.path
          d="m1378.07 471.5v-.11c0-6.37 5.17-9.7 12.69-9.7 3.79 0 6.43.52 9.07 1.26v-.92c0-5.28-3.21-8.04-8.78-8.04-3.5 0-6.26.86-9.02 2.12l-1.32-3.62c3.27-1.49 6.43-2.53 10.79-2.53s7.46 1.15 9.59 3.27 3.1 4.94 3.1 8.73v18.14h-4.36v-4.42c-2.07 2.64-5.45 5.05-10.68 5.05-5.57 0-11.08-3.16-11.08-9.25zm21.82-2.29v-2.76c-2.18-.63-5.05-1.26-8.67-1.26-5.51 0-8.67 2.41-8.67 5.97v.11c0 3.73 3.44 5.91 7.46 5.91 5.46 0 9.88-3.27 9.88-7.98zm-15.5-28.71h5.11v5.46h-5.11zm10.05 0h5.11v5.46h-5.11z"
          initial={luechinger130.initial}
          whileInView={luechinger130.animate}
          exit={luechinger130.exit}
          viewport={{ once: false }}
          transition={luechinger130.transition}
        />
        <motion.path
          d="m1419.23 468.86v-18.43h4.42v17.34c0 5.51 2.99 8.96 8.21 8.96s8.9-3.73 8.9-9.3v-17h4.36v29.69h-4.36v-5.17c-2.01 3.22-5.05 5.8-10.16 5.8-7.18 0-11.37-4.82-11.37-11.89z"
          initial={luechinger135.initial}
          whileInView={luechinger135.animate}
          exit={luechinger135.exit}
          viewport={{ once: false }}
          transition={luechinger135.transition}
        />
        <motion.path
          d="m1461.19 450.43h4.42v5c1.95-2.93 4.59-5.63 9.59-5.63s7.92 2.58 9.53 5.91c2.12-3.27 5.28-5.91 10.45-5.91 6.83 0 11.02 4.59 11.02 11.94v18.37h-4.42v-17.34c0-5.74-2.87-8.96-7.69-8.96-4.48 0-8.21 3.33-8.21 9.19v17.11h-4.36v-17.46c0-5.57-2.93-8.84-7.64-8.84s-8.27 3.9-8.27 9.36v16.94h-4.42v-29.69z"
          initial={luechinger140.initial}
          whileInView={luechinger140.animate}
          exit={luechinger140.exit}
          viewport={{ once: false }}
          transition={luechinger140.transition}
        />
      </motion.g>
    </svg>
  )
}

export default function Anniversary60Block({}: Anniversary60BlockProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)

  // Only render theme-dependent SVG after mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <div ref={containerRef} className="relative w-full h-screen overflow-hidden">
      {/* Container wrapper - centers logo */}
      <div className="container mx-auto px-8 h-full relative flex items-center justify-center">
        {/* Logo with viewport-triggered fade-in animation and text animations */}
        <motion.div
          className="relative w-full h-full flex items-center justify-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          {!mounted ? (
            // Render NegSVG during SSR to match initial client render
            <NegSVG />
          ) : theme === 'dark' ? (
            <PosSVG />
          ) : (
            <NegSVG />
          )}
        </motion.div>
      </div>
    </div>
  )
}
